import React, { useEffect, useMemo, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import { getStaffAttendanceList, getMessageStatusText } from "./Master";

import { formatToTime } from "../../master/FormatToDateTime";

import {
  staffAttendanceAbsentSendMessage,
  staffAttendancePost,
  staffAttendanceSendMessage,
} from "../../../api/Attendance";

import Swal from "sweetalert2";

export default function AttendanceStaffTable({
  filters,
  setFilters,
  refreshDashboard,
}) {
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    data: [],
    total: 0,
    present: 0,
    absent: 0,
    no_mark: 0,
  });

  /* =========================================================
     FETCH ATTENDANCE
  ========================================================= */

  const handleFunction = () => {
    getStaffAttendanceList({
      setLoading,
      filters,
      setData,
    });
  };

  useEffect(() => {
    handleFunction();
  }, [filters]);

  /* =========================================================
     ATTENDANCE HELPERS
  ========================================================= */

  const getAttendance = (item) => {
    return item?.attendances?.[0] ?? null;
  };

  /*
   * Attendance states:
   *
   * no attendance
   *      -> not_marked
   *
   * check_in = 2
   *      -> absent
   *
   * check_in = 1 + check_out = 2
   *      -> punched_in
   *
   * check_in = 1 + check_out = 1
   *      -> punched_out
   */

  const getAttendanceState = (item) => {
    const attendance = getAttendance(item);

    if (!attendance) {
      return "not_marked";
    }

    if (Number(attendance?.check_in) === 2) {
      return "absent";
    }

    if (Number(attendance?.check_in) === 1) {
      if (Number(attendance?.check_out) === 1) {
        return "punched_out";
      }

      return "punched_in";
    }

    return "not_marked";
  };

  /* =========================================================
     MESSAGE STATUS
  ========================================================= */

  const getMessageBadge = (status) => {
    const result = getMessageStatusText(status);

    switch (result?.text) {
      case "Sent":
        return (
          <span className="attendance-message-badge success">
            <i className="ri-check-line"></i>
            Sent
          </span>
        );

      case "Failed":
        return (
          <span className="attendance-message-badge danger">
            <i className="ri-close-line"></i>
            Failed
          </span>
        );

      case "Processing":
        return (
          <span className="attendance-message-badge warning">
            <i className="ri-loader-4-line"></i>
            Processing
          </span>
        );

      default:
        return (
          <span className="attendance-message-badge secondary">
            <i className="ri-mail-close-line"></i>
            Not Sent
          </span>
        );
    }
  };

  /* =========================================================
     SUMMARY CARDS
  ========================================================= */

  const attendanceCards = useMemo(
    () => [
      {
        label: "All Staff",
        value: "",
        showValue: data?.total ?? 0,
        color: "primary",
        icon: "ri-team-line",
      },
      {
        label: "Present",
        value: "1",
        showValue: data?.present ?? 0,
        color: "success",
        icon: "ri-user-follow-line",
      },
      {
        label: "Absent",
        value: "2",
        showValue: data?.absent ?? 0,
        color: "danger",
        icon: "ri-user-unfollow-line",
      },
      {
        label: "Not Marked",
        value: "0",
        showValue: data?.no_mark ?? 0,
        color: "warning",
        icon: "ri-user-forbid-line",
      },
    ],
    [data],
  );

  /* =========================================================
     SEND ATTENDANCE MESSAGE
  ========================================================= */

  const updateSendMessage = async () => {
    setLoading(true);

    try {
      let response;

      if (filters?.attendance === "2") {
        response = await staffAttendanceAbsentSendMessage();
      } else {
        response = await staffAttendanceSendMessage();
      }

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
      handleFunction();
      refreshDashboard?.();
    }
  };

  /* =========================================================
     UPDATE ATTENDANCE
  ========================================================= */

  const updateAttendance = async (registration_no, action) => {
    const currentTime = new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    let time = currentTime;

    /*
  |--------------------------------------------------------------------------
  | PUNCH IN / PUNCH OUT
  |--------------------------------------------------------------------------
  |
  | API expects type = "in" for attendance marking.
  | We use `action` only for frontend UI.
  |
  */

    if (action === "in" || action === "out") {
      const isPunchIn = action === "in";

      const { value: selectedTime } = await Swal.fire({
        title: isPunchIn ? "Select Punch In Time" : "Select Punch Out Time",

        input: "time",

        inputLabel: isPunchIn ? "Punch In Time" : "Punch Out Time",

        inputValue: currentTime,

        showCancelButton: true,

        confirmButtonText: isPunchIn ? "Punch In" : "Punch Out",

        cancelButtonText: "Cancel",

        confirmButtonColor: isPunchIn ? "#198754" : "#0d6efd",

        inputValidator: (value) => {
          if (!value) {
            return "Please select time";
          }

          return null;
        },
      });

      if (!selectedTime) {
        return;
      }

      time = selectedTime;
    }

    /*
  |--------------------------------------------------------------------------
  | ABSENT
  |--------------------------------------------------------------------------
  */

    if (action === "absent") {
      const result = await Swal.fire({
        title: "Mark Staff Absent?",

        text: "This staff member will be marked absent for the selected date.",

        icon: "warning",

        showCancelButton: true,

        confirmButtonText: "Mark Absent",

        cancelButtonText: "Cancel",

        confirmButtonColor: "#dc3545",
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setLoading(true);

    const formData = new FormData();

    formData.append("registration_no", registration_no);

    /*
  |--------------------------------------------------------------------------
  | IMPORTANT
  |--------------------------------------------------------------------------
  |
  | Your existing API expects "in" for the attendance
  | action. Do NOT send "out".
  |
  */

    if (action === "in" || action === "out") {
      formData.append("type", "in");
    } else {
      formData.append("type", "absent");
    }

    formData.append("date", filters?.date);

    formData.append("time", time);

    try {
      const response = await staffAttendancePost(formData);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);

      handleFunction();

      refreshDashboard?.();
    }
  };

  /* =========================================================
     ATTENDANCE ACTION BUTTONS
  ========================================================= */

  const renderNotMarked = (item) => {
    return (
      <div className="attendance-actions">
        {/* PUNCH IN */}

        <button
          type="button"
          className="attendance-action attendance-action-in"
          onClick={() => updateAttendance(item?.registration_no, "in")}
        >
          <i className="ri-login-box-line"></i>

          <span>Punch In</span>
        </button>

        {/* ABSENT */}

        <button
          type="button"
          className="attendance-action attendance-action-absent"
          onClick={() => updateAttendance(item?.registration_no, "absent")}
        >
          <i className="ri-user-unfollow-line"></i>

          <span>Absent</span>
        </button>
      </div>
    );
  };

  /* =========================================================
     ABSENT
  ========================================================= */

  const renderAbsent = (attendance) => {
    return (
      <div className="attendance-status-wrapper">
        <div className="attendance-status-row">
          <span className="attendance-status attendance-status-absent">
            <span className="attendance-status-icon">
              <i className="ri-close-line"></i>
            </span>

            <span>
              <small>Attendance</small>
              <strong>Absent</strong>
            </span>
          </span>

          {attendance?.check_in_time && (
            <span className="attendance-status-time">
              {formatToTime(attendance?.check_in_time)}
            </span>
          )}
        </div>

        <div className="attendance-message-row">
          {getMessageBadge(attendance?.absent_message_status)}
        </div>
      </div>
    );
  };

  /* =========================================================
     PUNCHED IN
  ========================================================= */

  const renderPunchedIn = (item, attendance) => {
    return (
      <div className="attendance-status-wrapper">
        <div className="attendance-punch-row">
          {/* IN TIME */}

          <div className="attendance-time-box in">
            <div className="attendance-time-icon">
              <i className="ri-login-box-line"></i>
            </div>

            <div>
              <small>Punch In</small>

              <strong>{formatToTime(attendance?.check_in_time)}</strong>
            </div>
          </div>

          {/* PUNCH OUT */}

          <button
            type="button"
            className="attendance-action attendance-action-out"
            onClick={() => updateAttendance(item?.registration_no, "out")}
          >
            <i className="ri-logout-box-line"></i>

            <span>Punch Out</span>
          </button>
        </div>

        <div className="attendance-message-row">
          {getMessageBadge(attendance?.in_message_status)}
        </div>
      </div>
    );
  };

  /* =========================================================
     PUNCHED OUT
  ========================================================= */

  const renderPunchedOut = (attendance) => {
    return (
      <div className="attendance-status-wrapper">
        <div className="attendance-timeline">
          {/* PUNCH IN */}

          <div className="attendance-time-box in">
            <div className="attendance-time-icon">
              <i className="ri-login-box-line"></i>
            </div>

            <div>
              <small>Punch In</small>

              <strong>{formatToTime(attendance?.check_in_time)}</strong>
            </div>
          </div>

          {/* CONNECTOR */}

          <div className="attendance-timeline-line">
            <i className="ri-arrow-right-line"></i>
          </div>

          {/* PUNCH OUT */}

          <div className="attendance-time-box out">
            <div className="attendance-time-icon">
              <i className="ri-logout-box-line"></i>
            </div>

            <div>
              <small>Punch Out</small>

              <strong>{formatToTime(attendance?.check_out_time)}</strong>
            </div>
          </div>
        </div>

        <div className="attendance-message-row">
          {getMessageBadge(attendance?.in_message_status)}

          {getMessageBadge(attendance?.out_message_status)}
        </div>
      </div>
    );
  };

  /* =========================================================
     ATTENDANCE RENDER
  ========================================================= */

  const renderAttendance = (item) => {
    const attendance = getAttendance(item);

    const state = getAttendanceState(item);

    if (state === "not_marked") {
      return renderNotMarked(item);
    }

    if (state === "absent") {
      return renderAbsent(attendance);
    }

    if (state === "punched_in") {
      return renderPunchedIn(item, attendance);
    }

    if (state === "punched_out") {
      return renderPunchedOut(attendance);
    }

    return "-";
  };

  /* =========================================================
     LOCATION
  ========================================================= */

  const renderLocation = (item) => {
    const attendance = getAttendance(item);

    if (!attendance) {
      return <span className="text-muted">-</span>;
    }

    return (
      <div className="attendance-location-wrapper">
        {attendance?.in_location && (
          <a
            href={`https://www.google.com/maps?q=${attendance.in_location}`}
            target="_blank"
            rel="noopener noreferrer"
            className="attendance-location"
          >
            <span className="attendance-location-icon in">
              <i className="ri-map-pin-line"></i>
            </span>

            <span>
              <small>In</small>
              View Location
            </span>
          </a>
        )}

        {attendance?.out_location && (
          <a
            href={`https://www.google.com/maps?q=${attendance.out_location}`}
            target="_blank"
            rel="noopener noreferrer"
            className="attendance-location"
          >
            <span className="attendance-location-icon out">
              <i className="ri-map-pin-line"></i>
            </span>

            <span>
              <small>Out</small>
              View Location
            </span>
          </a>
        )}

        {!attendance?.in_location && !attendance?.out_location && (
          <span className="text-muted">-</span>
        )}
      </div>
    );
  };

  /* =========================================================
     SOURCE
  ========================================================= */

  const renderSource = (item) => {
    const attendance = getAttendance(item);

    if (!attendance) {
      return <span className="text-muted">-</span>;
    }

    return (
      <div className="attendance-source">
        {Number(attendance?.check_in) === 1 && (
          <div className="attendance-source-item">
            <span className="attendance-source-label">In</span>

            <span>{attendance?.check_in_by ?? "-"}</span>
          </div>
        )}

        {Number(attendance?.check_out) === 1 && (
          <div className="attendance-source-item">
            <span className="attendance-source-label">Out</span>

            <span>{attendance?.check_out_by ?? "-"}</span>
          </div>
        )}
      </div>
    );
  };

  /* =========================================================
     GET STATE BADGE
  ========================================================= */

  const renderStaffState = (state) => {
    if (state === "punched_in") {
      return (
        <span className="staff-state working">
          <span></span>
          Working
        </span>
      );
    }

    if (state === "punched_out") {
      return (
        <span className="staff-state completed">
          <span></span>
          Completed
        </span>
      );
    }

    if (state === "absent") {
      return (
        <span className="staff-state absent">
          <span></span>
          Absent
        </span>
      );
    }

    return null;
  };

  /* =========================================================
     MAIN
  ========================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      {/* =====================================================
          SUMMARY CARDS
      ===================================================== */}

      <div className="row g-3 mb-4">
        {attendanceCards.map((type) => {
          const isActive = filters?.attendance === type?.value;

          return (
            <div className="col-xl-3 col-md-6 col-12" key={type.value || "all"}>
              <div
                className={`attendance-summary-card ${
                  isActive ? "active" : ""
                }`}
                style={{
                  "--attendance-color": `var(--bs-${type.color})`,

                  "--attendance-color-rgb": `var(--bs-${type.color}-rgb)`,
                }}
                onClick={() =>
                  setFilters({
                    ...filters,
                    attendance: type.value,
                  })
                }
              >
                {/* ICON */}

                <div className="attendance-summary-icon">
                  <i className={`${type.icon} ri-22px`}></i>
                </div>

                {/* CONTENT */}

                <div className="attendance-summary-content">
                  <div className="attendance-summary-label">{type.label}</div>

                  <div className="attendance-summary-value">
                    {type.showValue}
                  </div>
                </div>

                {/* ARROW */}

                <div className="attendance-summary-arrow">
                  <i className="ri-arrow-right-s-line ri-20px"></i>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* =====================================================
          TOOLBAR
      ===================================================== */}

      <div className="attendance-toolbar">
        <div>
          <div className="d-flex align-items-center gap-2">
            <div className="attendance-toolbar-icon">
              <i className="ri-calendar-check-line"></i>
            </div>

            <div>
              <h6 className="mb-0 fw-semibold">Staff Attendance</h6>

              <small className="text-muted">
                {filters?.date
                  ? `Attendance for ${filters.date}`
                  : "Daily attendance"}
              </small>
            </div>
          </div>
        </div>

        {filters?.attendance !== "0" && (
          <button
            type="button"
            className="attendance-message-button"
            onClick={updateSendMessage}
          >
            <i className="ri-message-2-line"></i>

            <span>
              Send{" "}
              {filters?.attendance === "1"
                ? "Present"
                : filters?.attendance === "2"
                  ? "Absent"
                  : "Marked"}{" "}
              Message
            </span>
          </button>
        )}
      </div>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <div className="table-responsive attendance-table-wrapper" id="PrintDiv">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th
                style={{
                  width: 60,
                }}
              >
                #
              </th>

              <th>Staff</th>

              <th>Mobile</th>

              <th>Attendance</th>

              {filters?.attendance === "1" && (
                <>
                  <th>Location</th>

                  <th>Source</th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {data?.data?.length > 0 ? (
              data.data.map((item, index) => {
                const state = getAttendanceState(item);

                return (
                  <tr key={item.id}>
                    {/* INDEX */}

                    <td>
                      <span className="text-muted fw-medium">{index + 1}</span>
                    </td>

                    {/* STAFF */}

                    <td>
                      <div className="d-flex align-items-center gap-3">
                        {/* AVATAR */}

                        <div
                          className={`staff-avatar ${
                            item?.gender === "Female" ? "female" : ""
                          }`}
                        >
                          {item?.name?.charAt(0)?.toUpperCase()}
                        </div>

                        {/* NAME */}

                        <div>
                          <div className="staff-name">{item?.name}</div>

                          <div className="staff-meta">
                            <span className="staff-registration">
                              {item?.registration_no}
                            </span>

                            {renderStaffState(state)}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* MOBILE */}

                    <td>
                      {item?.mobile ? (
                        <a
                          href={`tel:${item.mobile}`}
                          className="attendance-phone"
                        >
                          <span className="attendance-phone-icon">
                            <i className="ri-phone-line"></i>
                          </span>

                          {item.mobile}
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* ATTENDANCE */}

                    <td>{renderAttendance(item)}</td>

                    {/* LOCATION + SOURCE */}

                    {filters?.attendance === "1" && (
                      <>
                        <td>{renderLocation(item)}</td>

                        <td>{renderSource(item)}</td>
                      </>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={filters?.attendance === "1" ? 6 : 4}
                  className="text-center py-5"
                >
                  <div className="attendance-empty">
                    <div className="attendance-empty-icon">
                      <i className="ri-calendar-close-line"></i>
                    </div>

                    <div className="fw-semibold">
                      No attendance records found
                    </div>

                    <small className="text-muted">
                      Try changing the date or attendance filter.
                    </small>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* =====================================================
          STYLES
      ===================================================== */}

      <style>{`

        /* =====================================================
           SUMMARY CARDS
        ===================================================== */

        .attendance-summary-card {

          --attendance-color:
            var(--bs-primary);

          --attendance-color-rgb:
            var(--bs-primary-rgb);

          position: relative;

          min-height: 104px;

          padding: 18px 20px;

          display: flex;
          align-items: center;

          gap: 14px;

          background:
            var(--bs-card-bg);

          border:
            1px solid
            var(--bs-border-color);

          border-left:
            4px solid
            var(--attendance-color);

          border-radius:
            14px;

          cursor: pointer;

          color:
            var(--bs-body-color);

          transition:
            background-color .2s ease,
            border-color .2s ease,
            box-shadow .2s ease,
            transform .2s ease;
        }


        .attendance-summary-card:hover {

          border-color:
            var(--attendance-color);

          background:
            rgba(
              var(--attendance-color-rgb),
              .045
            );

          transform:
            translateY(-2px);

          box-shadow:
            0 5px 18px
            rgba(0, 0, 0, .08);
        }


        .attendance-summary-card.active {

          border-color:
            var(--attendance-color);

          background:
            rgba(
              var(--attendance-color-rgb),
              .11
            );

          box-shadow:
            0 0 0 1px
            rgba(
              var(--attendance-color-rgb),
              .10
            ),
            0 6px 20px
            rgba(0, 0, 0, .08);
        }


        /* =====================================================
           SUMMARY ICON
        ===================================================== */

        .attendance-summary-icon {

          width: 50px;
          height: 50px;

          min-width: 50px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius:
            13px;

          color:
            var(--attendance-color);

          background:
            rgba(
              var(--attendance-color-rgb),
              .12
            );

          transition:
            background-color .2s ease;
        }


        .attendance-summary-card:hover
        .attendance-summary-icon {

          background:
            rgba(
              var(--attendance-color-rgb),
              .17
            );
        }


        /* =====================================================
           SUMMARY TEXT
        ===================================================== */

        .attendance-summary-content {
          min-width: 0;
        }


        .attendance-summary-label {

          margin-bottom: 2px;

          font-size: 14px;

          font-weight: 500;

          color:
            var(--bs-secondary-color);
        }


        .attendance-summary-card.active
        .attendance-summary-label {

          color:
            var(--bs-body-color);
        }


        .attendance-summary-value {

          font-size: 30px;

          line-height: 1.1;

          font-weight: 700;

          color:
            var(--attendance-color);
        }


        /* =====================================================
           SUMMARY ARROW
        ===================================================== */

        .attendance-summary-arrow {

          margin-left: auto;

          width: 30px;
          height: 30px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius: 50%;

          color:
            var(--bs-secondary-color);

          transition:
            all .2s ease;
        }


        .attendance-summary-card:hover
        .attendance-summary-arrow {

          color:
            var(--attendance-color);

          background:
            rgba(
              var(--attendance-color-rgb),
              .10
            );

          transform:
            translateX(2px);
        }


        /* =====================================================
           TOOLBAR
        ===================================================== */

        .attendance-toolbar {

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 15px;

          flex-wrap: wrap;

          margin-bottom: 16px;

          padding:
            2px 0;
        }


        .attendance-toolbar-icon {

          width: 36px;
          height: 36px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius: 9px;

          color:
            var(--bs-primary);

          background:
            rgba(
              var(--bs-primary-rgb),
              .10
            );
        }


        .attendance-message-button {

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 7px;

          height: 36px;

          padding:
            0 14px;

          border-radius: 8px;

          border:
            1px solid
            rgba(
              var(--bs-warning-rgb),
              .25
            );

          color:
            var(--bs-warning);

          background:
            rgba(
              var(--bs-warning-rgb),
              .08
            );

          font-size: 13px;

          font-weight: 500;

          transition:
            all .2s ease;
        }


        .attendance-message-button:hover {

          color:
            var(--bs-white);

          background:
            var(--bs-warning);

          border-color:
            var(--bs-warning);
        }


        /* =====================================================
           TABLE
        ===================================================== */

        .attendance-table-wrapper {

          overflow: auto;

          border:
            1px solid
            var(--bs-border-color);

          border-radius:
            12px;

          background:
            var(--bs-card-bg);
        }


        .attendance-table-wrapper table {

          min-width:
            900px;
        }


        .attendance-table-wrapper thead th {

          padding:
            13px 15px;

          font-size:
            11px;

          font-weight:
            600;

          text-transform:
            uppercase;

          letter-spacing:
            .045em;

          white-space:
            nowrap;

          color:
            var(--bs-secondary-color);

          background:
            var(--bs-tertiary-bg);

          border-bottom:
            1px solid
            var(--bs-border-color);
        }


        .attendance-table-wrapper tbody td {

          padding:
            14px 15px;

          border-bottom:
            1px solid
            var(--bs-border-color);

          color:
            var(--bs-body-color);
        }


        .attendance-table-wrapper tbody tr:last-child td {

          border-bottom:
            0;
        }


        /* =====================================================
           STAFF
        ===================================================== */

        .staff-avatar {

          width: 42px;
          height: 42px;

          min-width: 42px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius:
            50%;

          background:
            rgba(
              var(--bs-primary-rgb),
              .12
            );

          color:
            var(--bs-primary);

          font-size:
            15px;

          font-weight:
            700;
        }


        .staff-avatar.female {

          background:
            rgba(
              var(--bs-danger-rgb),
              .12
            );

          color:
            var(--bs-danger);
        }


        .staff-name {

          font-size:
            14px;

          font-weight:
            600;

          line-height:
            1.3;
        }


        .staff-meta {

          display: flex;

          align-items: center;

          gap: 7px;

          margin-top:
            4px;

          flex-wrap:
            wrap;
        }


        .staff-registration {

          display: inline-flex;

          align-items: center;

          padding:
            2px 7px;

          border-radius:
            5px;

          font-size:
            11px;

          font-weight:
            600;

          color:
            var(--bs-primary);

          background:
            rgba(
              var(--bs-primary-rgb),
              .09
            );
        }


        /* =====================================================
           STAFF STATE
        ===================================================== */

        .staff-state {

          display: inline-flex;

          align-items: center;

          gap: 4px;

          font-size:
            11px;

          font-weight:
            500;
        }


        .staff-state > span {

          width: 6px;
          height: 6px;

          border-radius:
            50%;
        }


        .staff-state.working {

          color:
            var(--bs-success);
        }


        .staff-state.working > span {

          background:
            var(--bs-success);
        }


        .staff-state.completed {

          color:
            var(--bs-primary);
        }


        .staff-state.completed > span {

          background:
            var(--bs-primary);
        }


        .staff-state.absent {

          color:
            var(--bs-danger);
        }


        .staff-state.absent > span {

          background:
            var(--bs-danger);
        }


        /* =====================================================
           PHONE
        ===================================================== */

        .attendance-phone {

          display: inline-flex;

          align-items: center;

          gap: 7px;

          color:
            var(--bs-body-color);

          text-decoration:
            none;

          font-size:
            13px;
        }


        .attendance-phone:hover {

          color:
            var(--bs-primary);
        }


        .attendance-phone-icon {

          width: 28px;
          height: 28px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius:
            7px;

          color:
            var(--bs-primary);

          background:
            rgba(
              var(--bs-primary-rgb),
              .09
            );
        }


        /* =====================================================
           ATTENDANCE ACTIONS
        ===================================================== */

        .attendance-actions {

          display: flex;

          align-items: center;

          gap: 8px;

          flex-wrap: wrap;
        }


        .attendance-action {

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 6px;

          height: 34px;

          padding:
            0 12px;

          border-radius:
            8px;

          font-size:
            12px;

          font-weight:
            500;

          border:
            1px solid
            transparent;

          background:
            transparent;

          transition:
            all .2s ease;

          white-space:
            nowrap;
        }


        /* Punch In */

        .attendance-action-in {

          color:
            var(--bs-success);

          background:
            rgba(
              var(--bs-success-rgb),
              .09
            );

          border-color:
            rgba(
              var(--bs-success-rgb),
              .20
            );
        }


        .attendance-action-in:hover {

          color:
            var(--bs-white);

          background:
            var(--bs-success);

          border-color:
            var(--bs-success);
        }


        /* Punch Out */

        .attendance-action-out {

          color:
            var(--bs-primary);

          background:
            rgba(
              var(--bs-primary-rgb),
              .09
            );

          border-color:
            rgba(
              var(--bs-primary-rgb),
              .20
            );
        }


        .attendance-action-out:hover {

          color:
            var(--bs-white);

          background:
            var(--bs-primary);

          border-color:
            var(--bs-primary);
        }


        /* Absent */

        .attendance-action-absent {

          color:
            var(--bs-danger);

          background:
            rgba(
              var(--bs-danger-rgb),
              .07
            );

          border-color:
            rgba(
              var(--bs-danger-rgb),
              .18
            );
        }


        .attendance-action-absent:hover {

          color:
            var(--bs-white);

          background:
            var(--bs-danger);

          border-color:
            var(--bs-danger);
        }


        /* =====================================================
           ATTENDANCE STATUS
        ===================================================== */

        .attendance-status-wrapper {

          min-width:
            240px;
        }


        .attendance-status-row {

          display:
            flex;

          align-items:
            center;

          gap:
            10px;
        }


        .attendance-status {

          display:
            inline-flex;

          align-items:
            center;

          gap:
            8px;

          padding:
            6px 10px;

          border-radius:
            8px;
        }


        .attendance-status small {

          display:
            block;

          font-size:
            10px;

          color:
            var(--bs-secondary-color);
        }


        .attendance-status strong {

          display:
            block;

          font-size:
            13px;
        }


        .attendance-status-icon {

          width:
            28px;

          height:
            28px;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          border-radius:
            7px;
        }


        .attendance-status-absent {

          color:
            var(--bs-danger);

          background:
            rgba(
              var(--bs-danger-rgb),
              .08
            );
        }


        .attendance-status-absent
        .attendance-status-icon {

          background:
            rgba(
              var(--bs-danger-rgb),
              .13
            );
        }


        .attendance-status-time {

          font-size:
            12px;

          color:
            var(--bs-secondary-color);
        }


        /* =====================================================
           PUNCH TIME BOX
        ===================================================== */

        .attendance-punch-row {

          display:
            flex;

          align-items:
            center;

          gap:
            12px;

          flex-wrap:
            wrap;
        }


        .attendance-time-box {

          display:
            inline-flex;

          align-items:
            center;

          gap:
            8px;
        }


        .attendance-time-box small {

          display:
            block;

          font-size:
            10px;

          color:
            var(--bs-secondary-color);

          line-height:
            1.2;
        }


        .attendance-time-box strong {

          display:
            block;

          font-size:
            13px;

          line-height:
            1.3;
        }


        .attendance-time-icon {

          width:
            32px;

          height:
            32px;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          border-radius:
            8px;
        }


        .attendance-time-box.in
        .attendance-time-icon {

          color:
            var(--bs-success);

          background:
            rgba(
              var(--bs-success-rgb),
              .10
            );
        }


        .attendance-time-box.out
        .attendance-time-icon {

          color:
            var(--bs-primary);

          background:
            rgba(
              var(--bs-primary-rgb),
              .10
            );
        }


        /* =====================================================
           TIMELINE
        ===================================================== */

        .attendance-timeline {

          display:
            flex;

          align-items:
            center;

          gap:
            10px;
        }


        .attendance-timeline-line {

          width:
            28px;

          height:
            1px;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          color:
            var(--bs-secondary-color);
        }


        .attendance-timeline-line i {

          font-size:
            14px;
        }


        /* =====================================================
           MESSAGE BADGES
        ===================================================== */

        .attendance-message-row {

          display:
            flex;

          align-items:
            center;

          gap:
            6px;

          margin-top:
            7px;

          flex-wrap:
            wrap;
        }


        .attendance-message-badge {

          display:
            inline-flex;

          align-items:
            center;

          gap:
            4px;

          padding:
            3px 7px;

          border-radius:
            5px;

          font-size:
            10px;

          font-weight:
            500;
        }


        .attendance-message-badge.success {

          color:
            var(--bs-success);

          background:
            rgba(
              var(--bs-success-rgb),
              .09
            );
        }


        .attendance-message-badge.danger {

          color:
            var(--bs-danger);

          background:
            rgba(
              var(--bs-danger-rgb),
              .09
            );
        }


        .attendance-message-badge.warning {

          color:
            var(--bs-warning);

          background:
            rgba(
              var(--bs-warning-rgb),
              .10
            );
        }


        .attendance-message-badge.secondary {

          color:
            var(--bs-secondary-color);

          background:
            var(--bs-secondary-bg);
        }


        /* =====================================================
           LOCATION
        ===================================================== */

        .attendance-location-wrapper {

          display:
            flex;

          flex-direction:
            column;

          gap:
            7px;
        }


        .attendance-location {

          display:
            inline-flex;

          align-items:
            center;

          gap:
            7px;

          width:
            fit-content;

          color:
            var(--bs-body-color);

          text-decoration:
            none;

          font-size:
            12px;
        }


        .attendance-location:hover {

          color:
            var(--bs-primary);
        }


        .attendance-location > span:last-child {

          display:
            flex;

          flex-direction:
            column;
        }


        .attendance-location small {

          font-size:
            9px;

          color:
            var(--bs-secondary-color);
        }


        .attendance-location-icon {

          width:
            28px;

          height:
            28px;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          border-radius:
            7px;
        }


        .attendance-location-icon.in {

          color:
            var(--bs-success);

          background:
            rgba(
              var(--bs-success-rgb),
              .10
            );
        }


        .attendance-location-icon.out {

          color:
            var(--bs-primary);

          background:
            rgba(
              var(--bs-primary-rgb),
              .10
            );
        }


        /* =====================================================
           SOURCE
        ===================================================== */

        .attendance-source {

          display:
            flex;

          flex-direction:
            column;

          gap:
            6px;

          font-size:
            12px;
        }


        .attendance-source-item {

          display:
            flex;

          align-items:
            center;

          gap:
            7px;
        }


        .attendance-source-label {

          width:
            28px;

          padding:
            2px 5px;

          text-align:
            center;

          border-radius:
            5px;

          font-size:
            9px;

          font-weight:
            600;

          color:
            var(--bs-secondary-color);

          background:
            var(--bs-secondary-bg);
        }


        /* =====================================================
           EMPTY
        ===================================================== */

        .attendance-empty {

          display:
            flex;

          flex-direction:
            column;

          align-items:
            center;

          justify-content:
            center;
        }


        .attendance-empty-icon {

          width:
            52px;

          height:
            52px;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          margin-bottom:
            10px;

          border-radius:
            12px;

          color:
            var(--bs-secondary-color);

          background:
            var(--bs-secondary-bg);

          font-size:
            24px;
        }


        /* =====================================================
           DARK MODE
        ===================================================== */

        [data-bs-theme="dark"]
        .attendance-summary-card {

          background:
            var(--bs-card-bg);

          border-color:
            var(--bs-border-color);

          box-shadow:
            0 2px 8px
            rgba(0, 0, 0, .18);
        }


        [data-bs-theme="dark"]
        .attendance-summary-card:hover {

          background:
            rgba(
              var(--attendance-color-rgb),
              .08
            );

          border-color:
            var(--attendance-color);

          box-shadow:
            0 6px 20px
            rgba(0, 0, 0, .28);
        }


        [data-bs-theme="dark"]
        .attendance-summary-card.active {

          background:
            rgba(
              var(--attendance-color-rgb),
              .14
            );

          border-color:
            var(--attendance-color);

          box-shadow:
            0 0 0 1px
            rgba(
              var(--attendance-color-rgb),
              .12
            ),
            0 8px 24px
            rgba(0, 0, 0, .30);
        }


        [data-bs-theme="dark"]
        .attendance-table-wrapper {

          box-shadow:
            0 2px 8px
            rgba(0, 0, 0, .15);
        }


        /* =====================================================
           RESPONSIVE
        ===================================================== */

        @media (max-width: 768px) {

          .attendance-summary-card {

            min-height:
              90px;

            padding:
              14px 15px;

            border-radius:
              12px;

            gap:
              11px;
          }


          .attendance-summary-icon {

            width:
              44px;

            height:
              44px;

            min-width:
              44px;
          }


          .attendance-summary-value {

            font-size:
              25px;
          }


          .attendance-summary-label {

            font-size:
              13px;
          }


          .attendance-summary-arrow {

            width:
              26px;

            height:
              26px;
          }


          .attendance-toolbar {

            align-items:
              flex-start;
          }


          .attendance-status-wrapper {

            min-width:
              210px;
          }


          .attendance-timeline {

            align-items:
              flex-start;

            flex-direction:
              column;

            gap:
              8px;
          }


          .attendance-timeline-line {

            display:
              none;
          }

        }


        /* =====================================================
           PRINT
        ===================================================== */

        @media print {

          .attendance-summary-card {

            box-shadow:
              none !important;

            transform:
              none !important;
          }


          .attendance-table-wrapper {

            border:
              0 !important;

            overflow:
              visible !important;
          }


          .attendance-table-wrapper table {

            min-width:
              0 !important;
          }


          .attendance-message-button,
          .attendance-action {

            display:
              none !important;
          }

        }

      `}</style>
    </>
  );
}
